import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {usePage} from "@inertiajs/react";
import {Alert, Badge, Button, Container, Modal, Spinner, Table} from "react-bootstrap";
import axios from "axios";
import {EditorContent, useEditor} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import usePollingWhenVisible from "../../hooks/usePollingWhenVisible";

// Bezeichnung der Ticket-Art. Zwei Generationen von Schlüsseln:
// "vorschlag"/"hinweis" stammen aus dem Melde-Popup
// (SupportTicketController::TYPE_LABELS), "idee"/"frage" liefert das
// Portal, seit die Art dort ein eigenes Datenfeld ist. Beide müssen hier
// stehen - sonst zeigt die Liste den Rohwert klein geschrieben an.
const TYPE_LABELS = {
    problem: "Problem",
    vorschlag: "Verbesserungsvorschlag",
    hinweis: "Hinweis / Frage",
    idee: "Idee",
    frage: "Frage",
};

// Meldungen der Art "Problem" bekommen eine zart rote Zeile: In einer
// Liste aus Fragen und Ideen soll sofort auffallen, wo etwas kaputt ist.
//
// Gesetzt wird die Bootstrap-Variable, nicht schlicht backgroundColor:
// Die Tabelle hat Zebra-Streifen, und die färben in Bootstrap 5 die
// ZELLEN (über --bs-table-bg-type), nicht die Zeile. Ein
// Zeilen-Hintergrund läge darunter und wäre in jeder zweiten Zeile
// unsichtbar. Über die Variable erben die Zellen die Farbe.
const PROBLEM_ZEILE = { "--bs-table-bg-type": "#fdf3f3" };

// Dieselben Limits wie im Melde-Popup (SupportTicketModal).
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_MB = 8;

/**
 * Ordnet einen Status-Schlüssel/Text einer der vier vereinbarten Farben zu.
 * Die genauen Status-Schlüssel des Portals stehen noch nicht endgültig fest
 * (die Schnittstelle entsteht parallel) - deshalb wird per Stichwort im
 * normalisierten Text gematcht statt auf exakte Werte zu bauen. Unbekannte
 * Status fallen auf Grau zurück und zeigen trotzdem den Klartext an.
 */
function statusFarbe(status, statusLabel) {
    const text = `${status ?? ""} ${statusLabel ?? ""}`
        .toLowerCase()
        .replaceAll("ä", "ae")
        .replaceAll("ö", "oe")
        .replaceAll("ü", "ue");

    if (/(erledigt|geschlossen|closed|done|gel[oö]st)/.test(text)) {
        return {background: "#198754", color: "#fff"}; // grün
    }
    if (/(wartet|warten|r[uü]ckmeldung|pending.?reply)/.test(text)) {
        return {background: "#ffc107", color: "#212529"}; // gelb
    }
    if (/(in.?arbeit|bearbeitung|progress)/.test(text)) {
        return {background: "#fd7e14", color: "#fff"}; // orange
    }
    if (/(neu|offen|open|new)/.test(text)) {
        return {background: "#0d6efd", color: "#fff"}; // blau
    }

    return {background: "#6c757d", color: "#fff"}; // grau (unbekannter Status)
}

function StatusPille({status, statusLabel}) {
    const farbe = statusFarbe(status, statusLabel);

    return (
        <span
            className="badge rounded-pill"
            style={{backgroundColor: farbe.background, color: farbe.color}}
        >
            {statusLabel || status || "Unbekannt"}
        </span>
    );
}

function formatTimestamp(value) {
    if (!value) {
        return "–";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "–";
    }
    return date.toLocaleString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

/**
 * Vereinheitlicht den Sichtbarkeits-Vertrag während des gestuften Rollouts.
 * Das neue Portal liefert `visibility`; bis dahin fehlt das Feld und ein
 * eigenes Ticket fällt sicher auf „Nur für mich“ zurück. Auch flache Felder
 * werden akzeptiert, damit der API-Vertrag additive Erweiterungen verträgt.
 */
function ticketSichtbarkeit(ticket) {
    const visibility = ticket?.visibility ?? {};
    const scope = visibility.scope ?? ticket?.visibility_scope ?? "private";
    const departmentNames = visibility.department_names ?? ticket?.visibility_department_names ?? [];
    const meetingVisible = Boolean(visibility.meeting_visible ?? ticket?.meeting_visible ?? false);

    let label = visibility.label ?? ticket?.visibility_label;
    if (!label) {
        if (scope === "all") {
            label = "Alle Benutzer";
        } else if (scope === "departments") {
            label = departmentNames.length > 0
                ? `Abteilung${departmentNames.length > 1 ? "en" : ""}: ${departmentNames.join(", ")}`
                : "Eigene Abteilung";
        } else {
            label = "Nur für mich";
        }
    }

    const order = scope === "private" ? 10 : scope === "departments" ? 20 : 30;

    return {
        scope,
        label,
        meetingVisible,
        groupKey: `${order}:${scope}:${departmentNames.join("|")}`,
        order,
    };
}

function sichtbarkeitsGruppen(tickets) {
    const groups = new Map();

    tickets.forEach((ticket) => {
        const visibility = ticketSichtbarkeit(ticket);
        if (!groups.has(visibility.groupKey)) {
            groups.set(visibility.groupKey, {
                key: visibility.groupKey,
                label: visibility.label,
                order: visibility.order,
                tickets: [],
            });
        }
        groups.get(visibility.groupKey).tickets.push(ticket);
    });

    return [...groups.values()].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, "de"));
}

function Sichtbarkeit({ticket}) {
    const visibility = ticketSichtbarkeit(ticket);

    return (
        <div className="d-flex flex-wrap align-items-center gap-1">
            <span>{visibility.label}</span>
            {visibility.meetingVisible && (
                <Badge bg="light" text="dark" className="border">Meeting</Badge>
            )}
        </div>
    );
}

/**
 * Vorschaugalerie für Ticket-Anhänge. Bild-Anhänge erscheinen als
 * quadratische Vorschaukacheln (120 × 120, dezent gerundet); ein Klick
 * vergrößert sie im eigenen Lichtkasten auf Bildschirmgröße statt sie nur
 * in einem neuen Tab zu öffnen. Herunterladen bleibt möglich: über den
 * Link im Lichtkasten. Die Anhang-Route selbst bleibt unangetastet -
 * dieselbe geprüfte Route wie bisher, nur angezeigt statt geladen.
 *
 * Der Lichtkasten-Zustand lebt in dieser Komponente, die ein Kind des
 * Ticket-Popups ist. Das Popup lädt seine Daten selbst und wird vom
 * Listen-Poll alle 45 s nicht berührt - eine offene Vergrößerung geht
 * dadurch weder zu noch flackert sie.
 */
function AnhangGalerie({externalId, anhänge, basisPfad = "/support/tickets"}) {
    const [offeneId, setOffeneId] = useState(null);

    const urlFür = useCallback(
        (id) => `${basisPfad}/${encodeURIComponent(externalId)}/anhang/${encodeURIComponent(id)}`,
        [externalId, basisPfad]
    );

    // Blättern erfolgt nur über die Bild-Anhänge - ein Nicht-Bild dazwischen
    // würde beim Weiterblättern nur eine leere Fläche zeigen.
    const bilder = useMemo(
        () => anhänge.filter((a) => (a.mime || "").startsWith("image/")),
        [anhänge]
    );

    const offenIndex = offeneId !== null ? bilder.findIndex((b) => b.id === offeneId) : -1;

    return (
        <>
            <div className="d-flex flex-wrap gap-2">
                {anhänge.map((anhang) => (
                    <AnhangKachel
                        key={anhang.id}
                        anhang={anhang}
                        url={urlFür(anhang.id)}
                        onOpen={() => setOffeneId(anhang.id)}
                    />
                ))}
            </div>
            {offenIndex >= 0 && (
                <BildLightbox
                    bilder={bilder}
                    index={offenIndex}
                    urlFür={urlFür}
                    onIndex={(i) => setOffeneId(bilder[i].id)}
                    onSchliessen={() => setOffeneId(null)}
                />
            )}
        </>
    );
}

/**
 * Eine einzelne Anhang-Kachel. Bei Bildern quadratische Vorschau mit
 * Vergrößern-Klick; schlägt das Laden fehl (Anhang gelöscht, Portal weg),
 * wird einmalig auf den Dateinamen-Download zurückgefallen - beim nächsten
 * Aktualisieren nicht erneut probiert, also weder Flackern noch eine
 * kaputte Symbolgrafik. loading="lazy" und feste Maße halten das Layout
 * beim Laden ruhig.
 */
function AnhangKachel({anhang, url, onOpen}) {
    const istBild = (anhang.mime || "").startsWith("image/");
    const [kaputt, setKaputt] = useState(false);
    const name = anhang.original_name || "Anhang";
    const masse = {height: 120, width: 120};

    // Kein Bild oder Laden fehlgeschlagen: Dateiname als Download-Link,
    // kein kaputtes Bild-Symbol.
    if (!istBild || kaputt) {
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={name}
                className="d-inline-flex align-items-center justify-content-center border rounded text-decoration-none text-muted px-2 text-center"
                style={{...masse, fontSize: "0.72rem", lineHeight: 1.1, overflow: "hidden"}}
            >
                {name}
            </a>
        );
    }

    return (
        <button
            type="button"
            onClick={onOpen}
            className="btn p-0 border-0"
            style={masse}
            aria-label={`Bild vergrößern: ${name}`}
            title={name}
        >
            <img
                src={url}
                alt={name}
                loading="lazy"
                onError={() => setKaputt(true)}
                className="rounded border"
                style={{...masse, objectFit: "cover", display: "block"}}
            />
        </button>
    );
}

/**
 * Lichtkasten: zeigt ein Anhang-Bild in voller Größe. Schließen per Klick
 * auf die Fläche daneben und per Escape-Taste; mit den Pfeiltasten lässt
 * sich durch alle Bilder der Galerie blättern (falls mehrere). Eigene
 * Umsetzung im Bootstrap-Stil, kein confirm() und kein nativer Dialog.
 *
 * Escape wird in der Capture-Phase abgefangen und nicht weitergereicht -
 * sonst ginge beim Schließen des Lichtkastens versehentlich auch das
 * darunterliegende Ticket-Popup zu (react-overlays lauscht im Bubble-Phase
 * auf Escape). Per Portal an document.body gerendert, damit der Lichtkasten
 * zuverlässig über dem Bootstrap-Modal samt Backdrop liegt.
 */
function BildLightbox({bilder, index, urlFür, onIndex, onSchliessen}) {
    const anhang = bilder[index];
    const url = urlFür(anhang.id);
    const name = anhang.original_name || "Anhang";
    const mehrere = bilder.length > 1;
    const schliessenRef = useRef(null);

    // Fokus auf den Schließen-Knopf setzen, damit Tastatur-Nutzer dort
    // landen und Escape/Tab sinnvoll greifen.
    useEffect(() => {
        schliessenRef.current?.focus();
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                e.preventDefault();
                onSchliessen();
            } else if (e.key === "ArrowRight" && index < bilder.length - 1) {
                e.stopPropagation();
                onIndex(index + 1);
            } else if (e.key === "ArrowLeft" && index > 0) {
                e.stopPropagation();
                onIndex(index - 1);
            }
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [index, bilder.length, onIndex, onSchliessen]);

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`Bild: ${name}`}
            onClick={onSchliessen}
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 1080,
                background: "rgba(0,0,0,0.88)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
                cursor: "zoom-out",
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: "relative",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12,
                    cursor: "default",
                }}
            >
                <img
                    src={url}
                    alt={name}
                    style={{
                        maxWidth: "100%",
                        maxHeight: "82vh",
                        objectFit: "contain",
                        borderRadius: 8,
                        background: "#fff",
                        display: "block",
                    }}
                />
                <div className="d-flex align-items-center gap-3">
                    {mehrere && (
                        <span className="small" style={{color: "rgba(255,255,255,0.75)"}}>
                            {index + 1} / {bilder.length}
                        </span>
                    )}
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="small text-white text-decoration-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        ⬇ Herunterladen
                    </a>
                </div>
            </div>

            <button
                ref={schliessenRef}
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onSchliessen();
                }}
                className="btn btn-light btn-sm d-flex align-items-center justify-content-center"
                aria-label="Bild schließen"
                style={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    borderRadius: "50%",
                    width: 40,
                    height: 40,
                    cursor: "pointer",
                }}
            >
                ✕
            </button>

            {mehrere && index > 0 && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onIndex(index - 1);
                    }}
                    className="btn btn-light btn-sm d-flex align-items-center justify-content-center"
                    aria-label="Vorheriges Bild"
                    style={{
                        position: "absolute",
                        left: 16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        borderRadius: "50%",
                        width: 44,
                        height: 44,
                        fontSize: "1.4rem",
                        lineHeight: 1,
                        cursor: "pointer",
                    }}
                >
                    ‹
                </button>
            )}
            {mehrere && index < bilder.length - 1 && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onIndex(index + 1);
                    }}
                    className="btn btn-light btn-sm d-flex align-items-center justify-content-center"
                    aria-label="Nächstes Bild"
                    style={{
                        position: "absolute",
                        right: 16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        borderRadius: "50%",
                        width: 44,
                        height: 44,
                        fontSize: "1.4rem",
                        lineHeight: 1,
                        cursor: "pointer",
                    }}
                >
                    ›
                </button>
            )}
        </div>,
        document.body
    );
}

/**
 * Quadratisches Vorschaubild in der Ticket-Liste - nur wenn am Ticket ein
 * Bild-Anhang hängt. Die ID des ersten Bild-Anhangs kommt als
 * preview_attachment_id aus der Listen-Antwort des Portals; fehlt sie (noch
 * nicht ausgespielt oder gar kein Screenshot am Ticket), wird nichts
 * gerendert - kein Platzhalter, keine leere Box, die die Tabelle unruhig
 * macht.
 *
 * Das Bild läuft über die vorhandene Anhang-Route, die serverseitig
 * durchstreamt (die Portal-Zugangsdaten bleiben dadurch im Backend). Ein
 * Klick öffnet das Ticket wie jeder Klick in der Zeile - das Bild hat kein
 * eigenes Verhalten, es erbt den Klick der klickbaren Tabellenzeile.
 *
 * Flackern bei der automatischen Aktualisierung alle 45 Sekunden: Die
 * Komponente bleibt dank stabilem Zeilen-Key (external_id) an derselben
 * Stelle stehen, src und img-Element ändern sich nicht - der Browser-Cache
 * liefert das Bild sofort, ohne Neuladen. Schlägt das Laden fehl (Anhang
 * gelöscht, Portal weg), merkt sich der Komponenten-Status das einmalig und
 * blendet das Bild still aus - beim nächsten Poll wird nicht erneut
 * probiert, also weder Flackern noch ein kaputtes Bild-Symbol.
 */
function ListenVorschau({src, alt}) {
    const [versteckt, setVersteckt] = useState(false);

    if (versteckt) {
        return null;
    }

    return (
        <img
            src={src}
            alt={alt}
            width={44}
            height={44}
            loading="lazy"
            onError={() => setVersteckt(true)}
            className="rounded border flex-shrink-0"
            style={{width: 44, height: 44, objectFit: "cover"}}
        />
    );
}

/**
 * Ein Eintrag im Antwortverlauf, dargestellt als Chat-Blase.
 *
 * "ausgehend" ist eine Antwort des Supports (Thorsten) an den Melder -
 * erscheint LINKS in einer grauen Blase mit blauem Akzent-Streifen.
 * "eingehend" ist eine Rückmeldung des Melders (z. B. Julia selbst) -
 * erscheint RECHTS in einer blauen Blase. Das entspricht der iMessage-/
 * WhatsApp-Konvention: die eigene Seite (Melder) rechts, die Gegenseite
 * (Support) links.
 *
 * Die Autoren-Kennung kommt ausschließlich aus author_label (z. B.
 * "Thorsten" oder "Julia"); ist das Feld leer, fällt es auf richtungs-
 * basiert "Support" bzw. "Melder" zurück. Ein hartcodiertes Präfix wie
 * früher ("Antwort vom Support – Support") gibt es nicht mehr - das
 * doppelte "Support" war der Fehler.
 */
function VerlaufEintrag({externalId, eintrag, basisPfad}) {
    const istSupport = eintrag.richtung === "ausgehend";
    const anhänge = eintrag.attachments || [];
    const autor = eintrag.author_label || (istSupport ? "Support" : "Melder");

    // Blase: links grau mit Primary-Akzent, rechts blau mit weißem Text.
    const blasenKlasse = istSupport
        ? "bg-light border border-primary-subtle"
        : "bg-primary text-white";
    const zeitKlasse = istSupport ? "text-muted" : "text-white-50";

    return (
        <div className={`d-flex mb-2 ${istSupport ? "justify-content-start" : "justify-content-end"}`}>
            <div
                className={`p-2 rounded ${blasenKlasse}`}
                style={{
                    maxWidth: "80%",
                    borderLeft: istSupport ? "4px solid #0d6efd" : undefined,
                }}
            >
                <div className="d-flex justify-content-between align-items-baseline mb-1 gap-2">
                    <span className="fw-semibold small">Antwort von {autor}</span>
                    <span className={`small ${zeitKlasse}`}>{formatTimestamp(eintrag.created_at)}</span>
                </div>
                <div className="small" style={{whiteSpace: "pre-wrap"}}>{eintrag.body}</div>
                {anhänge.length > 0 && (
                    <div className="mt-2">
                        <AnhangGalerie externalId={externalId} anhänge={anhänge} basisPfad={basisPfad} />
                    </div>
                )}
            </div>
        </div>
    );
}

function TicketDetailModal({externalId, onHide, assignedMode = false}) {
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Nachlegen (weitere Antwort) und Schließen - eigene Eingabezustände,
    // getrennt vom Ticket-Laden.
    const [attachments, setAttachments] = useState([]);
    const [sendingReply, setSendingReply] = useState(false);
    const [replyError, setReplyError] = useState(null);
    const [closing, setClosing] = useState(false);
    const [closeError, setCloseError] = useState(null);
    const [confirmClose, setConfirmClose] = useState(false);
    const fileInputRef = useRef(null);

    // Derselbe Editor-Baustein wie im Melde-Popup (SupportTicketModal):
    // StarterKit + Unterstrichen + Link + Platzhalter. So sieht der
    // Mitarbeiter in beiden Fällen dieselbe Formatierung.
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({openOnClick: false}),
            Placeholder.configure({placeholder: "Antwort schreiben …"}),
        ],
        content: "",
    });

    useEffect(() => {
        if (!externalId) {
            return;
        }
        let aktiv = true;
        setLoading(true);
        setError(null);
        setTicket(null);

        const basisPfad = assignedMode ? "/support/tickets/zugewiesen" : "/support/tickets";
        axios
            .get(`${basisPfad}/${encodeURIComponent(externalId)}`)
            .then(({data}) => {
                if (aktiv) {
                    setTicket(data);
                }
            })
            .catch(() => {
                if (aktiv) {
                    setError("Das Ticket konnte gerade nicht geladen werden. Bitte versuche es gleich noch einmal.");
                }
            })
            .finally(() => {
                if (aktiv) {
                    setLoading(false);
                }
            });

        return () => {
            aktiv = false;
        };
    }, [externalId, assignedMode]);

    // Beim Wechsel auf ein anderes Ticket die Eingaben zurücksetzen - eine
    // angefangene Antwort gehört zum vorherigen Ticket.
    useEffect(() => {
        setAttachments([]);
        setReplyError(null);
        setCloseError(null);
        setConfirmClose(false);
        editor?.commands.clearContent();
    }, [externalId, editor]);

    const kommentare = [...(ticket?.comments ?? [])].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    // Dasselbe Prädikat wie in statusFarbe(): ist das Ticket schon erledigt,
    // wird das Schließen nicht mehr angeboten.
    const istErledigt = useMemo(() => {
        const text = `${ticket?.status ?? ""} ${ticket?.status_label ?? ""}`.toLowerCase();
        return /erledigt|geschlossen|closed|done|gel[oö]st/.test(text);
    }, [ticket]);

    // Freigegebene Tickets sind im ersten Vertrag bewusst lesbar, aber nur
    // der Melder darf antworten oder schließen. Das Portal liefert dafür
    // explizite Fähigkeiten. Beim alten Portal fehlen sie; eigene Tickets
    // behalten dann exakt ihr bisheriges Verhalten.
    const canComment = !assignedMode && (ticket?.capabilities?.can_comment ?? ticket?.is_reporter ?? true);
    const canClose = !assignedMode && (ticket?.capabilities?.can_close ?? ticket?.is_reporter ?? true);

    // Screenshots wahlweise per Dateiauswahl oder direkt aus der Zwischenablage
    // (Strg+V) - die niedrigste Hürde, wie im Melde-Popup.
    const addFiles = useCallback((files) => {
        setReplyError(null);
        setAttachments((prev) => {
            const next = [...prev];
            for (const file of files) {
                if (next.length >= MAX_ATTACHMENTS) {
                    setReplyError(`Höchstens ${MAX_ATTACHMENTS} Screenshots pro Antwort.`);
                    break;
                }
                if (!/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) {
                    setReplyError("Nur Bilder (PNG, JPG, GIF, WebP) sind möglich.");
                    continue;
                }
                if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
                    setReplyError(`"${file.name}" ist größer als ${MAX_ATTACHMENT_MB} MB.`);
                    continue;
                }
                next.push(file);
            }
            return next;
        });
    }, []);

    const handlePaste = useCallback(
        (e) => {
            const files = [...(e.clipboardData?.files || [])];
            if (files.length > 0) {
                addFiles(files);
            }
        },
        [addFiles]
    );

    const previews = useMemo(
        () => attachments.map((file) => ({file, url: URL.createObjectURL(file)})),
        [attachments]
    );

    const sendReply = async () => {
        // Spinner aktiv: kein Doppelklick (dasselbe Problem wie beim
        // Excel-Export - das erste Senden muss durchkommen).
        if (sendingReply) {
            return;
        }
        const html = editor?.getHTML() ?? "";
        const text = editor?.getText() ?? "";

        if (!text.trim()) {
            setReplyError("Bitte eine Nachricht eintragen.");
            return;
        }

        setReplyError(null);
        setSendingReply(true);
        try {
            const form = new FormData();
            form.append("body_html", html);
            form.append("body", text);
            attachments.forEach((file) => form.append("attachments[]", file));

            const {data} = await axios.post(
                `/support/tickets/${encodeURIComponent(externalId)}/kommentar`,
                form
            );

            // Neuen Eintrag sofort in den Verlauf aufnehmen - ohne Neuladen.
            // Das Popup bleibt offen, die Eingabe wird zurückgesetzt.
            setTicket((prev) =>
                prev
                    ? {...prev, comments: [...(prev.comments ?? []), data.comment]}
                    : prev
            );
            setAttachments([]);
            editor?.commands.clearContent();
        } catch (e) {
            // Den geschriebenen Text bewusst NICHT verwerfen - sonst tippt
            // jemand alles neu. Nur melden, dass es nicht geklappt hat.
            setReplyError(
                e.response?.data?.message ||
                    "Die Antwort konnte gerade nicht gesendet werden. Bitte versuche es gleich noch einmal."
            );
        } finally {
            setSendingReply(false);
        }
    };

    const closeTicket = async () => {
        if (closing) {
            return;
        }
        setCloseError(null);
        setClosing(true);
        try {
            const {data} = await axios.patch(
                `/support/tickets/${encodeURIComponent(externalId)}/status`
            );
            // Status sofort im Popup aktualisieren - Pille wird grün, Schließen
            // wird ausgeblendet.
            setTicket((prev) =>
                prev
                    ? {...prev, status: data.status, status_label: data.status_label}
                    : prev
            );
            setConfirmClose(false);
        } catch (e) {
            setCloseError(
                e.response?.data?.message ||
                    "Das Ticket konnte gerade nicht geschlossen werden. Bitte versuche es gleich noch einmal."
            );
        } finally {
            setClosing(false);
        }
    };

    // Formatierungs-Knopf für die Werkzeugleiste - derselbe Baustein wie im
    // Melde-Popup (onMouseDown statt onClick, damit der Editor den Fokus behält).
    const editorButton = (label, action, isActive) => (
        <Button
            size="sm"
            variant={isActive ? "secondary" : "outline-secondary"}
            className="px-2 py-0"
            onMouseDown={(e) => {
                e.preventDefault();
                action();
            }}
        >
            {label}
        </Button>
    );

    return (
        <Modal show={externalId !== null} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title className="fs-5">{ticket?.subject || "Ticket"}</Modal.Title>
            </Modal.Header>
            <Modal.Body onPaste={handlePaste}>
                {loading && (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                    </div>
                )}
                {!loading && error && <Alert variant="danger">{error}</Alert>}
                {!loading && !error && ticket && (
                    <>
                        <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                            <StatusPille status={ticket.status} statusLabel={ticket.status_label} />
                            <Badge bg="light" text="dark" className="border">
                                {TYPE_LABELS[ticket.typ] || ticket.typ}
                            </Badge>
                            {(ticket.systeme || []).map((system) => (
                                <Badge key={system} bg="secondary">{system}</Badge>
                            ))}
                            <div className="border rounded px-2 py-1 small bg-light">
                                <Sichtbarkeit ticket={ticket} />
                            </div>
                            <span className="text-muted small ms-auto">
                                Eingereicht am {formatTimestamp(ticket.created_at)}
                            </span>
                        </div>

                        <div
                            className="mb-3"
                            dangerouslySetInnerHTML={{__html: ticket.message_html || ""}}
                        />

                        {(ticket.attachments || []).length > 0 && (
                            <div className="mb-3">
                                <div className="text-muted small text-uppercase mb-1" style={{letterSpacing: "0.04em"}}>
                                    Screenshots
                                </div>
                                <AnhangGalerie externalId={externalId} anhänge={ticket.attachments} basisPfad={assignedMode ? "/support/tickets/zugewiesen" : "/support/tickets"} />
                            </div>
                        )}

                        {kommentare.length > 0 && (
                            <div className="mb-3">
                                <div className="text-muted small text-uppercase mb-2" style={{letterSpacing: "0.04em"}}>
                                    Verlauf
                                </div>
                                {kommentare.map((eintrag) => (
                                    <VerlaufEintrag key={eintrag.id} externalId={externalId} eintrag={eintrag} basisPfad={assignedMode ? "/support/tickets/zugewiesen" : "/support/tickets"} />
                                ))}
                            </div>
                        )}

                        {/* Nachlegen: Editor mit Formatierung + Screenshots wie
                            beim ersten Absenden. */}
                        {canComment && <div className="border-top pt-3">
                            <div className="text-muted small text-uppercase mb-2" style={{letterSpacing: "0.04em"}}>
                                Antwort schreiben
                            </div>
                            <div className="border rounded">
                                <div className="d-flex gap-1 border-bottom p-1">
                                    {editorButton(<strong>F</strong>, () => editor.chain().focus().toggleBold().run(), editor?.isActive("bold"))}
                                    {editorButton(<em>K</em>, () => editor.chain().focus().toggleItalic().run(), editor?.isActive("italic"))}
                                    {editorButton(<u>U</u>, () => editor.chain().focus().toggleUnderline().run(), editor?.isActive("underline"))}
                                    {editorButton("• Liste", () => editor.chain().focus().toggleBulletList().run(), editor?.isActive("bulletList"))}
                                    {editorButton("1. Liste", () => editor.chain().focus().toggleOrderedList().run(), editor?.isActive("orderedList"))}
                                </div>
                                <EditorContent
                                    editor={editor}
                                    className="p-2 support-editor"
                                />
                            </div>

                            <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                                {previews.map(({file, url}, i) => (
                                    <div key={url} className="position-relative">
                                        <img
                                            src={url}
                                            alt={file.name}
                                            style={{height: 64, width: 64, objectFit: "cover"}}
                                            className="rounded border"
                                        />
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            className="position-absolute top-0 end-0 py-0 px-1"
                                            style={{transform: "translate(30%, -30%)", lineHeight: 1.2}}
                                            aria-label={`${file.name} entfernen`}
                                            onClick={() =>
                                                setAttachments((prev) => prev.filter((_, j) => j !== i))
                                            }
                                        >
                                            ×
                                        </Button>
                                    </div>
                                ))}
                                {attachments.length < MAX_ATTACHMENTS && (
                                    <>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            + Screenshot
                                        </Button>
                                        <span className="text-muted small">oder mit Strg+V einfügen</span>
                                    </>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/gif,image/webp"
                                    multiple
                                    hidden
                                    onChange={(e) => {
                                        addFiles([...e.target.files]);
                                        e.target.value = "";
                                    }}
                                />
                            </div>

                            {replyError && (
                                <Alert variant="danger" className="py-2 mt-2 mb-0">
                                    {replyError}
                                </Alert>
                            )}

                            <div className="d-flex justify-content-end mt-2">
                                <Button variant="primary" disabled={sendingReply} onClick={sendReply}>
                                    {sendingReply ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Wird gesendet…
                                        </>
                                    ) : (
                                        "Antwort senden"
                                    )}
                                </Button>
                            </div>
                        </div>}

                        {!assignedMode && !canComment && (
                            <Alert variant="light" className="border mt-3 mb-0 py-2">
                                Dieses Ticket wurde für dich freigegeben. Antworten und Abschließen bleiben beim Ersteller.
                            </Alert>
                        )}
                    </>
                )}
            </Modal.Body>

            {/* Schließen: bewusst zurückhaltend (Link-Optik) und mit eigener
                Rückfrage statt confirm(). Bei erledigten Tickets entfällt das. */}
            {canClose && !loading && !error && ticket && !istErledigt && (
                <Modal.Footer className="justify-content-start align-items-center">
                    {closeError && (
                        <Alert variant="danger" className="py-2 mb-0 w-100">
                            {closeError}
                        </Alert>
                    )}
                    {!confirmClose ? (
                        <Button
                            variant="link"
                            className="text-muted px-0"
                            onClick={() => setConfirmClose(true)}
                        >
                            Auf Erledigt setzen
                        </Button>
                    ) : (
                        <div className="d-flex flex-wrap align-items-center gap-2 w-100">
                            <span className="text-muted small me-auto">
                                Ticket auf „Erledigt" setzen? Der Support bearbeitet es dann nicht weiter.
                            </span>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={closing}
                                onClick={() => {
                                    setConfirmClose(false);
                                    setCloseError(null);
                                }}
                            >
                                Abbrechen
                            </Button>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={closing}
                                onClick={closeTicket}
                            >
                                {closing ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-1" />
                                        Wird geschlossen…
                                    </>
                                ) : (
                                    "Auf Erledigt setzen"
                                )}
                            </Button>
                        </div>
                    )}
                </Modal.Footer>
            )}
        </Modal>
    );
}

/**
 * "Tickets": Liste aller für den angemeldeten Benutzer sichtbaren Tickets,
 * nach ihrer Freigabe gruppiert. Die Daten kommen live vom Portal - ist es
 * gerade nicht erreichbar, zeigt die Seite eine verständliche Meldung statt
 * abzustürzen (siehe loadError-Prop).
 */
export default function MyTickets() {
    const {tickets, loadError, assignedMode} = usePage().props;
    const [eigeneTickets, setEigeneTickets] = useState(tickets ?? []);
    const [aktivesTicket, setAktivesTicket] = useState(null);

    // Tickets im Hintergrund aktualisieren, ohne die Seite neu zu laden.
    // Das offene Detail-Popup bleibt unberührt: Es lädt seine Daten
    // selbst (eigener Abruf in TicketDetailModal) und hängt nicht am
    // Listen-State. Bei Portal-Ausfall (error=true) bleibt die bisherige
    // Liste einfach stehen - kein Wegspringen, keine Fehlermeldung. Der
    // Poll pausiert automatisch bei Tabs im Hintergrund.
    usePollingWhenVisible(assignedMode ? null : "/support/tickets/stand", 45000, (data) => {
        if (data && data.error !== true) {
            setEigeneTickets(Array.isArray(data.tickets) ? data.tickets : []);
        }
    });

    const schliessen = useCallback(() => setAktivesTicket(null), []);

    const liste = eigeneTickets;
    const gruppen = assignedMode
        ? [{key: "assigned", label: "Mir zugewiesen", tickets: liste}]
        : sichtbarkeitsGruppen(liste);

    return (
        <Container fluid className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1>{assignedMode ? "Mir zugewiesene Tickets" : "Tickets"}</h1>
            </div>

            <div className="d-flex gap-2 mb-3"><a className={`btn btn-sm ${!assignedMode ? "btn-primary" : "btn-outline-primary"}`} href="/support/tickets">Sichtbare Tickets</a><a className={`btn btn-sm ${assignedMode ? "btn-primary" : "btn-outline-primary"}`} href="/support/tickets/zugewiesen">Mir zugewiesen</a></div>
            {loadError && <Alert variant="warning">{loadError}</Alert>}

            {!loadError && liste.length === 0 && (
                <Alert variant="light" className="border">
                    {assignedMode ? "Dir sind aktuell keine Tickets zugewiesen." : "Für dich sind aktuell keine Tickets sichtbar."}
                </Alert>
            )}

            {!loadError && liste.length > 0 && gruppen.map((gruppe) => (
                <section key={gruppe.key} className="mb-4">
                    {!assignedMode && (
                        <h2 className="h6 text-uppercase text-muted mb-2" style={{letterSpacing: "0.04em"}}>
                            {gruppe.label} <span className="fw-normal">({gruppe.tickets.length})</span>
                        </h2>
                    )}
                    <Table striped bordered hover responsive className="align-middle mb-0">
                        <thead>
                        <tr>
                            <th>Betreff</th>
                            <th>Art</th>
                            <th>Status</th>
                            <th>Sichtbarkeit</th>
                            <th>Eingereicht am</th>
                            <th>Antworten</th>
                        </tr>
                        </thead>
                        <tbody>
                        {gruppe.tickets.map((ticket) => (
                            <tr
                                key={ticket.external_id}
                                role="button"
                                style={ticket.typ === "problem" ? PROBLEM_ZEILE : undefined}
                                onClick={() => setAktivesTicket(ticket.external_id)}
                            >
                                <td>
                                    <div className="d-flex align-items-center gap-2">
                                        {ticket.preview_attachment_id ? (
                                            <ListenVorschau
                                                src={`${assignedMode ? "/support/tickets/zugewiesen" : "/support/tickets"}/${encodeURIComponent(ticket.external_id)}/anhang/${encodeURIComponent(ticket.preview_attachment_id)}`}
                                                alt=""
                                            />
                                        ) : null}
                                        <span>{ticket.subject}</span>
                                    </div>
                                </td>
                                <td>{TYPE_LABELS[ticket.typ] || ticket.typ}</td>
                                <td>
                                    <StatusPille status={ticket.status} statusLabel={ticket.status_label} />
                                </td>
                                <td><Sichtbarkeit ticket={ticket} /></td>
                                <td className="text-nowrap">{formatTimestamp(ticket.created_at)}</td>
                                <td className="text-nowrap">
                                    {ticket.reply_count > 0
                                        ? `${ticket.reply_count} · zuletzt ${formatTimestamp(ticket.last_reply_at)}`
                                        : "–"}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </section>
            ))}

            {aktivesTicket !== null && (
                <TicketDetailModal externalId={aktivesTicket} onHide={schliessen} assignedMode={assignedMode} />
            )}
        </Container>
    );
}
